import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";

const createPOSOpeningEntry = (branchName, formData) => {
    const params = {
        doc: JSON.stringify(formData),
        action: `Submit`,
    };
    try {
        return axios.post(`method/frappe.desk.form.save.savedocs`, params);
    } catch (err) {
        throw new err();
    }
};

export const usePOSEntryCreateData = () => {
    return useMutation(async ({ branchId, formData }) => {
        const data = await createPOSOpeningEntry(branchId, formData);
        return data;
    });
};

export const useCreateOpeningEntry = () => {
    return useMutation(async ({ company, pos_profile, balance_details }) => {
        const payload = {
            company,
            pos_profile,
            balance_details: JSON.stringify(balance_details),
        };
        const data = await axios.post(
            "method/erpnext.selling.page.point_of_sale.point_of_sale.create_opening_voucher",
            payload
        );
        return data;
    });
};

export const useGetParyDetails = () => {
    return useMutation(async ({ company, party_type, party, date }) => {
        const payload = {
            company,
            party_type,
            party,
            date,
        };
        const data = await axios.post(
            "method/erpnext.accounts.doctype.payment_entry.payment_entry.get_party_details",
            payload
        );
        return data;
    });
};

export const useGetIncomingRate = () => {
    return useMutation(async ({ args }) => {
        const payload = {
            args,
        };
        const data = await axios.post(
            "method/erpnext.stock.utils.get_incoming_rate",
            payload
        );
        return data;
    });
};

const createPOSClosingEntry = (formData) => {
    const params = {
        // doctype:`POS Closing Entry`,
        doc: JSON.stringify(formData),
        action: `Submit`,
    };
    try {
        return axios.post(`method/frappe.desk.form.save.savedocs`, params);
    } catch (err) {
        throw new err();
    }
};

export const usePOSEntryCloseCreateData = () => {
    return useMutation(async ({ formData }) => {
        const data = await createPOSClosingEntry(formData);
        return data;
    });
};

export const useFetchPosLinkedInvoices = () => {
    return useMutation(async ({ startDate, endDate, posProfile, user }) => {
        const { data } = await getClosingEntryInvoices(
            startDate,
            endDate,
            posProfile,
            user
        );
        return data;
    });
};

export const useGetSalesData = ({
    company,
    pos_profile,
    // start,
    // end,
    opening_amount,
}) => {
    const data = useQuery(
        ["useGetSalesData", pos_profile],
        async () => {
            const params = {
                company,
                pos_profile,
                // start,
                // end,
                opening_amount,
            };
            const response = await axios.get(
                `method/restaurant_management.api.get_sales`,
                { params }
            );

            const _meta = response.data.message;
            _meta.data = _meta.fields;
            return _meta;
        },
        {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchOnReconnect: false,
            enabled: !!pos_profile,
            cacheTime: Infinity,
        }
    );

    return data;
};

//TODO WAS HERE .. CREATE MUTATION INSTEAD OF useQuery for getClosingEntryInvoices and implement in OpeningEntries

const getClosingEntryInvoices = (startDate, endDate, posProfile, user) => {
    const params = {
        start: startDate,
        end: endDate,
        pos_profile: posProfile,
        user: user,
    };
    try {
        return axios.post(
            `method/erpnext.accounts.doctype.pos_closing_entry.pos_closing_entry.get_pos_invoices`,
            params
        );
    } catch (err) {
        throw new err();
    }
};

export const useGetClosingInvoicesData = (
    startDate,
    endDate,
    posProfile,
    user,
    onSuccess,
    onError
) => {
    return useQuery(
        ["GetClosingInvoices", startDate, endDate],
        async () => {
            const { data } = await getClosingEntryInvoices(
                startDate,
                endDate,
                posProfile,
                user
            );
            return data.message;
        },
        {
            onSuccess,
            onError,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            enabled: !!startDate && !!endDate && !!posProfile && !!user,
        }
    );
};

const closeCash = ({
    company,
    pos_profile,
    expected_amount,
    amount_in_register,
    opening_amount,
    credit_card_amount,
}) => {
    const params = {
        company,
        pos_profile,
        opening_amount,
        expected_amount,
        amount_in_register,
        credit_card_amount,
    };
    try {
        return axios.post(
            `method/restaurant_management.api.close_cash`,
            params
        );
    } catch (err) {
        throw new err();
    }
};

export const useCloseCash = () => {
    return useMutation(
        async ({
            company,
            pos_profile,
            opening_amount,
            expected_amount,
            amount_in_register,
            credit_card_amount,
        }) => {
            const data = await closeCash({
                company,
                pos_profile,
                expected_amount,
                amount_in_register,
                opening_amount,
                credit_card_amount,
            });
            return data;
        }
    );
};

export const useCloseShiftEntry = () => {
    const summarizeAmountInRegister = (rows) => {
        try {
            if (!Array.isArray(rows)) return rows;
            return rows.map((r) => ({
                mode_of_payment: r?.mode_of_payment,
                opening_amount: r?.opening_amount,
                expected_amount: r?.expected_amount,
                closing_amount: r?.closing_amount,
                difference: r?.difference,
            }));
        } catch {
            return "[unserializable]";
        }
    };

    return useMutation(
        async ({ amount_in_register, pos_opening_entry, cash_transactinos }) => {
            const payload = {
                amount_in_register,
                pos_opening_entry,
            };
            if (cash_transactinos !== undefined) {
                payload.cash_transactinos = cash_transactinos;
            }

            try {
                const data = await axios.post(
                    "method/restaurant_management.api.close_shift_entry",
                    payload
                );
                return data.data.message;
            } catch (err) {
                const status = err?.response?.status;
                const responseData = err?.response?.data;
                const serverMessage =
                    responseData?.message ||
                    responseData?.exc ||
                    responseData?._server_messages ||
                    responseData?.error ||
                    responseData;

                console.error("[close_shift_entry] request failed", {
                    status,
                    pos_opening_entry,
                    cash_transactinos_present:
                        cash_transactinos !== undefined && cash_transactinos !== null,
                    amount_in_register: summarizeAmountInRegister(amount_in_register),
                    serverMessage,
                    error: err?.message,
                });

                // Preserve original error for callers/onError, but attach a readable message.
                const message =
                    (typeof serverMessage === "string" && serverMessage) ||
                    (typeof responseData?.message === "string" && responseData.message) ||
                    err?.message ||
                    "close_shift_entry failed";
                throw new Error(message);
            }
        }
    );
};

// Fetch cash-out transactions for POS closing
const getCashOutTransactions = (startDate, endDate, company, user) => {
    const params = {
        start_date: startDate,
        end_date: endDate,
        company: company,
        user: user,
    };
    try {
        return axios.post(
            `method/restaurant_management.api.get_cashout_transactions`,
            params
        );
    } catch (err) {
        throw new err();
    }
};

export const useGetCashOutTransactions = (
    startDate,
    endDate,
    company,
    user,
    onSuccess,
    onError
) => {
    return useQuery(
        ["GetCashOutTransactions", startDate, endDate, company, user],
        async () => {
            const { data } = await getCashOutTransactions(
                startDate,
                endDate,
                company,
                user
            );
            return data.message;
        },
        {
            onSuccess,
            onError,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            enabled: !!startDate && !!endDate && !!company && !!user,
        }
    );
};

const searchPOSOpenining = ({ user }) => {
    const params = {
        user: user,
    };
    try {
        return axios.get(
            `method/erpnext.selling.page.point_of_sale.point_of_sale.check_opening_entry`,
            { params }
        );
    } catch (err) {
        throw new err();
    }
};

// const searchPOSOpenining = ({ user }) => {
//     const params = {
//         fields: JSON.stringify(["*"]),
//         expand: JSON.stringify(["balance_details"]),
//         limit: 0,
//     };

//     if (user) {
//         params.filters = JSON.stringify([
//             ["status", "=", "open"],
//             ["docstatus", "=", "1"],
//             ["user", "=", user],
//         ]);
//     } else {
//         params.filters = JSON.stringify([
//             ["status", "=", "open"],
//             ["docstatus", "=", "1"],
//         ]);
//     }
//     try {
//         return axios.get(`resource/POS Opening Entry`, { params });
//     } catch (err) {
//         throw new err();
//     }
// };

export const useSearchPOSOpening = ({ user }, onSuccess, onError) => {
    return useQuery(
        ["searchPOSOpenining", user],
        async () => {
            const { data } = await searchPOSOpenining({ user });
            return data.message;
        },
        {
            onSuccess,
            onError,
            refetchOnMount: true,
            refetchOnWindowFocus: false,
            refetchOnReconnect: true,
            cacheTime: 30 * 1000 * 60 * 60 * 24,
            enabled: !!user, // Only run query when user is available
        }
    );
};

export const useGetPosEntryDetails = (name) => {
    return useQuery(
        ["getPosEntryDetails", name],
        async () => {
            const { data } = await fetchDocType("POS Opening Entry", name);
            return data.message;
        },
        {
            enabled: !!name,
            cacheTime: 0,
            refetchOnMount: true,
            refetchOnReconnect: true,
        }
    );
};

export const useGetShiftEntryDetails = ({ pos_opening_entry }) => {
    return useQuery(
        ["getPosEntryDetails", pos_opening_entry],
        async () => {
            const payload = {
                pos_opening_entry: pos_opening_entry,
            };
            const { data } = await axios.post(
                `method/restaurant_management.api.close_shift_entry_from_opening`,
                payload
            );
            return data.message;
        },
        {
            enabled: !!pos_opening_entry,
            cacheTime: 0,
            refetchOnMount: true,
            refetchOnReconnect: true,
        }
    );
};

const getExchangeRateAdjustmentJournalEntries = async ({
    startDate,
    endDate,
    company,
}) => {
    const params = {
        doctype: "Journal Entry",
        fields: JSON.stringify(["name"]),
        filters: JSON.stringify([
            ["docstatus", "=", 1],
            ["posting_date", ">=", startDate],
            ["posting_date", "<=", endDate],
            ["company", "=", company],
            ["title", "like", "Exchange Rate Adjustment for%"],
        ]),
        limit_page_length: 0,
    };

    const { data } = await axios.get("method/frappe.client.get_list", { params });
    return data?.message || [];
};

const getJournalEntryDoc = async (name) => {
    const params = {
        doctype: "Journal Entry",
        name,
    };
    const { data } = await axios.get("method/frappe.client.get", { params });
    return data?.message;
};

export const useGetExchangeRateAdjustments = (
    startDate,
    endDate,
    company,
    companyCurrency
) => {
    return useQuery(
        [
            "GetExchangeRateAdjustments",
            startDate,
            endDate,
            company,
            companyCurrency,
        ],
        async () => {
            const entries = await getExchangeRateAdjustmentJournalEntries({
                startDate,
                endDate,
                company,
            });
            if (!entries?.length) return {};

            const perCurrency = {};
            for (const e of entries) {
                const doc = await getJournalEntryDoc(e?.name);
                const accounts = doc?.accounts || [];
                accounts.forEach((row) => {
                    const currency = row?.account_currency;
                    if (!currency || currency === companyCurrency) return;
                    if ((row?.account_type || "").toLowerCase() !== "cash") return;

                    const amountInCurrency = Number(row?.debit_in_account_currency) || 0;
                    const amountInCompanyCurrency = Number(row?.debit) || 0;
                    if (amountInCurrency <= 0 || amountInCompanyCurrency <= 0) return;

                    if (!perCurrency[currency]) {
                        perCurrency[currency] = {
                            currency,
                            amount_in_currency: 0,
                            amount_in_company_currency: 0,
                        };
                    }
                    perCurrency[currency].amount_in_currency += amountInCurrency;
                    perCurrency[currency].amount_in_company_currency +=
                        amountInCompanyCurrency;
                });
            }

            return Object.fromEntries(
                Object.entries(perCurrency).map(([currency, totals]) => {
                    const rate =
                        totals.amount_in_currency > 0
                            ? totals.amount_in_company_currency /
                              totals.amount_in_currency
                            : 0;
                    return [currency, { ...totals, exchange_rate: rate }];
                })
            );
        },
        {
            enabled:
                !!startDate &&
                !!endDate &&
                !!company &&
                !!companyCurrency,
            refetchOnWindowFocus: false,
            refetchOnMount: true,
            refetchOnReconnect: true,
        }
    );
};

const validateLink = ({ doctype, docname, fields }) => {
    const params = {
        doctype,
        docname,
        fields: JSON.stringify(fields || []),
    };

    return axios.post(`method/frappe.client.validate_link`, params);
};

export const useValidateLink = (
    doctype,
    docname,
    fields,
    onSuccess,
    onError
) => {
    const fieldsKey = JSON.stringify(fields || []);
    return useQuery(
        ["validateLink", doctype, docname, fieldsKey],
        async () => {
            const { data } = await validateLink({ doctype, docname, fields });
            return data.message;
        },
        {
            onSuccess,
            onError,
            enabled: !!doctype && !!docname,
            refetchOnWindowFocus: false,
            refetchOnMount: true,
            refetchOnReconnect: true,
        }
    );
};

const fetchDocType = (doctype, name) => {
    const params = {
        doctype,
        name,
    };

    return axios.get(`method/frappe.client.get`, { params });
};

export const useSaveAndSubmit = () => {
    return useMutation(async ({ doc }) => {
        const payload = {
            doc,
        };
        const data = await axios.post(
            "method/ui_builder.api.save_and_submit",
            payload
        );
        return data;
    });
};
